import React, { useState, useEffect } from 'react';
import { fastapiService } from '../../services/fastapiService';

interface AvailablePhoneNumber {
  id: string;
  phone_number: string;
  country_code: string;
  cost: number;
  currency: string;
  location?: string;
  is_available: boolean;
  notes?: string;
  added_at: string;
  reserved_status?: string;
  verification_status?: string;
  phone_numbers?: Array<{
    id: string;
    status: string;
    nickname?: string;
    monthly_cost?: number;
  }>;
}

interface Verification {
  id: string;
  phone_number_id: string;
  company_name: string;
  documents: any;
  verification_status: 'pending' | 'approved' | 'rejected' | 'documents_required';
  verification_notes?: string;
  submitted_at: string;
  reviewed_at?: string;
  phone_numbers?: {
    phone_number: string;
    status?: string;
    workspace_id?: string;
    workspaces?: {
      id: string;
      name: string;
    };
  };
}

export const PhoneNumbersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'available' | 'verifications'>('available');
  const [availableNumbers, setAvailableNumbers] = useState<AvailablePhoneNumber[]>([]);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState<Verification | null>(null);
  
  // Add form state
  const [formData, setFormData] = useState({
    phone_number: '',
    country_code: 'IN',
    cost: 1000,
    currency: 'INR',
    location: '',
    notes: ''
  });

  useEffect(() => {
    if (activeTab === 'available') {
      loadAvailableNumbers();
      const interval = setInterval(() => loadAvailableNumbers(), 30000);
      return () => clearInterval(interval);
    } else {
      loadVerifications();
      const interval = setInterval(() => loadVerifications(), 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const loadAvailableNumbers = async () => {
    setLoading(true);
    try {
      const response = await fastapiService.request('/admin/phone-numbers/available', {
        params: { country_code: 'IN' },
        timeout: 30000
      });
      if (response.success) {
        setAvailableNumbers(response.data || []);
      } else {
        console.error('Error loading available numbers:', response.error);
      }
    } catch (error: any) {
      console.error('Error loading available numbers:', error);
      const errMsg = error?.error || error?.message || '';
      if (!errMsg.toLowerCase().includes('timeout')) {
        alert('Error loading available numbers: ' + (errMsg || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVerifications = async () => {
    setLoading(true);
    try {
      const response = await fastapiService.request('/admin/phone-numbers/verifications', {
        params: { status: 'pending' },
        timeout: 30000
      });
      if (response.success) {
        setVerifications(response.data || []);
      } else {
        console.error('Error loading verifications:', response.error);
      }
    } catch (error: any) {
      console.error('Error loading verifications:', error);
      const errMsg = error?.error || error?.message || '';
      if (!errMsg.toLowerCase().includes('timeout')) {
        alert('Error loading verifications: ' + (errMsg || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddNumber = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fastapiService.request('/admin/phone-numbers/available', {
        method: 'POST',
        body: formData
      });
      if (response.success) {
        setShowAddModal(false);
        setFormData({
          phone_number: '',
          country_code: 'IN',
          cost: 1000,
          currency: 'INR',
          location: '',
          notes: ''
        });
        loadAvailableNumbers();
      } else {
        alert('Error: ' + (response.error || 'Failed to add number'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to add number'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = async (id: string, currentStatus: boolean) => {
    if (!confirm(`Are you sure you want to ${currentStatus ? 'remove' : 'make available'} this number?`)) {
      return;
    }
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/available/${id}`, {
        method: 'PATCH',
        body: { is_available: !currentStatus }
      });
      if (response.success) {
        loadAvailableNumbers();
      } else {
        alert('Error: ' + (response.error || 'Failed to update'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to update'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAvailableNumber = async (id: string, updates: { cost?: number; is_available?: boolean; notes?: string }) => {
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/available/${id}`, {
        method: 'PATCH',
        body: updates
      });
      if (response.success) {
        alert('Phone number updated successfully!');
        loadAvailableNumbers();
      } else {
        alert('Error: ' + (response.error || 'Failed to update'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to update'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNumber = async (id: string) => {
    if (!confirm('Are you sure you want to delete this number from the pool?')) {
      return;
    }
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/available/${id}`, {
        method: 'DELETE'
      });
      if (response.success) {
        loadAvailableNumbers();
      } else {
        alert('Error: ' + (response.error || 'Failed to delete'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to delete'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (verificationId: string) => {
    if (!confirm('Approve this verification request?')) {
      return;
    }
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/verifications/${verificationId}/approve`, {
        method: 'POST',
        body: { notes: 'Approved by admin' }
      });
      if (response.success) {
        alert('Verification approved successfully!');
        loadVerifications();
        setShowVerificationModal(null);
      } else {
        alert('Error: ' + (response.error || 'Failed to approve'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to approve'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (verificationId: string, reason: string) => {
    if (!reason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/verifications/${verificationId}/reject`, {
        method: 'POST',
        body: { notes: reason }
      });
      if (response.success) {
        alert('Verification rejected successfully!');
        loadVerifications();
        setShowVerificationModal(null);
      } else {
        alert('Error: ' + (response.error || 'Failed to reject'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to reject'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhoneNumber = async (phoneNumberId: string, updates: { nickname?: string; monthly_cost?: number }) => {
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/${phoneNumberId}`, {
        method: 'PATCH',
        body: updates
      });
      if (response.success) {
        alert('Phone number updated successfully!');
        loadVerifications();
        loadAvailableNumbers(); // Also refresh available numbers to show updated status
        setShowVerificationModal(null);
      } else {
        alert('Error: ' + (response.error || 'Failed to update'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to update'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDocuments = async (verificationId: string, notes: string) => {
    if (!notes.trim()) {
      alert('Please specify what documents are needed');
      return;
    }
    setLoading(true);
    try {
      const response = await fastapiService.request(`/admin/phone-numbers/verifications/${verificationId}/request-documents`, {
        method: 'POST',
        body: { notes }
      });
      if (response.success) {
        alert('Document request sent successfully!');
        loadVerifications();
        setShowVerificationModal(null);
      } else {
        alert('Error: ' + (response.error || 'Failed to send request'));
      }
    } catch (error: any) {
      alert('Error: ' + (error.error || error.message || 'Failed to send request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Phone Numbers Management</h1>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('available')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Available Numbers ({availableNumbers.length})
          </button>
          <button
            onClick={() => setActiveTab('verifications')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'verifications'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Verifications ({verifications.length})
          </button>
        </nav>
      </div>

      {/* Available Numbers Tab */}
      {activeTab === 'available' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              + Add Phone Number
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : availableNumbers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No available phone numbers</div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {availableNumbers.map((number) => (
                    <tr key={number.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {number.phone_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{number.country_code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <span>{number.currency} {number.cost}</span>
                          {number.phone_numbers && number.phone_numbers.length > 0 && (
                            <button
                              onClick={() => {
                                const phone = number.phone_numbers[0];
                                const currentCost = phone.monthly_cost?.toString() || number.cost.toString();
                                const newCost = prompt(`Edit monthly cost (${number.currency}):`, currentCost);
                                if (newCost !== null && newCost !== currentCost) {
                                  const parsedCost = parseFloat(newCost);
                                  if (!isNaN(parsedCost)) {
                                    handleEditPhoneNumber(phone.id, { monthly_cost: parsedCost });
                                  }
                                }
                              }}
                              className="text-blue-600 hover:text-blue-800 text-xs underline"
                              title="Edit price"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{number.location || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {number.reserved_status ? (
                          <div className="space-y-1">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              number.reserved_status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : number.reserved_status === 'pending'
                                  ? number.verification_status === 'approved'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {number.reserved_status === 'active' 
                                ? 'Active' 
                                : number.reserved_status === 'pending'
                                  ? number.verification_status === 'approved'
                                    ? 'Approved - Payment Pending'
                                    : 'Pending Verification'
                                  : number.reserved_status}
                            </span>
                            {number.verification_status && (
                              <div className="text-xs text-gray-500">
                                Verification: {number.verification_status}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            number.is_available
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {number.is_available ? 'Available' : 'Unavailable'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(number.added_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {number.phone_numbers && number.phone_numbers.length > 0 && (
                            <button
                              onClick={() => {
                                const phone = number.phone_numbers[0];
                                setShowVerificationModal({
                                  ...number,
                                  phone_numbers: phone
                                } as Verification);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-xs"
                              title="View details"
                            >
                              View
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const newCost = prompt(`Edit cost (${number.currency}):`, number.cost.toString());
                              if (newCost !== null) {
                                const parsedCost = parseFloat(newCost);
                                if (!isNaN(parsedCost)) {
                                  handleUpdateAvailableNumber(number.id, { cost: parsedCost });
                                }
                              }
                            }}
                            className="text-primary-600 hover:text-primary-900 text-xs"
                            title="Edit cost"
                          >
                            Edit Cost
                          </button>
                          <button
                            onClick={() => handleToggleAvailability(number.id, number.is_available)}
                            className="text-primary-600 hover:text-primary-900 text-xs"
                          >
                            {number.is_available ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleDeleteNumber(number.id)}
                            className="text-red-600 hover:text-red-900 text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Verifications Tab */}
      {activeTab === 'verifications' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : verifications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No pending verifications</div>
          ) : (
            <div className="space-y-4">
              {verifications.map((verification) => (
                <div key={verification.id} className="bg-white shadow rounded-lg p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {verification.phone_numbers?.phone_number || 'Unknown Number'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Company: {verification.company_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Workspace: {verification.phone_numbers?.workspaces?.name || 'Unknown'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Submitted: {new Date(verification.submitted_at).toLocaleString()}
                      </p>
                      <p className="text-sm font-medium mt-2">
                        Status: {verification.verification_status === 'approved' 
                          ? (verification.phone_numbers?.status === 'pending' 
                              ? 'Approved - Payment Pending' 
                              : verification.phone_numbers?.status === 'active' 
                                ? 'Approved - Active' 
                                : `Approved - ${verification.phone_numbers?.status || 'Pending'}`)
                          : (verification.verification_status === 'pending' || verification.verification_status === 'documents_required')
                            ? 'Pending Verification'
                            : verification.verification_status.charAt(0).toUpperCase() + verification.verification_status.slice(1).replace('_', ' ')}
                      </p>
                      {verification.verification_notes && (
                        <p className="text-sm text-gray-600 mt-2">
                          Notes: {verification.verification_notes}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowVerificationModal(verification)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleApprove(verification.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          const reason = prompt('Rejection reason:');
                          if (reason) handleReject(verification.id, reason);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Number Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Add Phone Number</h2>
            <form onSubmit={handleAddNumber}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="text"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="+91 172 488 9080"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country Code</label>
                  <input
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost</label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location (Optional)</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows={3}
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  Add Number
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Verification Details Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Verification Details</h2>
            <div className="space-y-4">
              <div>
                <strong>Phone Number:</strong> {showVerificationModal.phone_numbers?.phone_number}
              </div>
              <div>
                <strong>Company:</strong> {showVerificationModal.company_name}
              </div>
              <div>
                <strong>Workspace:</strong> {showVerificationModal.phone_numbers?.workspaces?.name}
              </div>
              <div>
                <strong>Verification Status:</strong> {showVerificationModal.verification_status === 'approved' 
                  ? (showVerificationModal.phone_numbers?.status === 'pending' 
                      ? 'Approved - Payment Pending' 
                      : showVerificationModal.phone_numbers?.status === 'active' 
                        ? 'Approved - Active' 
                        : `Approved - ${showVerificationModal.phone_numbers?.status || 'Pending'}`)
                  : (showVerificationModal.verification_status === 'pending' || showVerificationModal.verification_status === 'documents_required')
                    ? 'Pending Verification'
                    : showVerificationModal.verification_status.charAt(0).toUpperCase() + showVerificationModal.verification_status.slice(1).replace('_', ' ')}
              </div>
              <div>
                <strong>Phone Number Status:</strong> {showVerificationModal.phone_numbers?.status || 'N/A'}
              </div>
              <div>
                <strong>Documents:</strong>
                <div className="mt-2 space-y-2">
                  {showVerificationModal.documents && typeof showVerificationModal.documents === 'object' ? (
                    (() => {
                      const docs = showVerificationModal.documents;
                      // Check if documents_by_type exists (new structure)
                      if (docs.documents_by_type && typeof docs.documents_by_type === 'object') {
                        return Object.entries(docs.documents_by_type).map(([docType, doc]: [string, any]) => (
                          <div key={docType} className="border p-2 rounded">
                            <strong>{docType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
                            {doc.file_url ? (
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                                View Document ({doc.original_filename || doc.filename || 'File'})
                              </a>
                            ) : doc.url ? (
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                                View Document
                              </a>
                            ) : (
                              <span className="text-gray-500 ml-2">No URL available</span>
                            )}
                          </div>
                        ));
                      }
                      // Check if uploaded_documents exists (array structure)
                      else if (docs.uploaded_documents && Array.isArray(docs.uploaded_documents)) {
                        return docs.uploaded_documents.map((doc: any, index: number) => (
                          <div key={index} className="border p-2 rounded">
                            <strong>Document {index + 1}:</strong>{' '}
                            {doc.file_url ? (
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                                {doc.original_filename || doc.filename || 'View Document'}
                              </a>
                            ) : doc.url ? (
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                                View Document
                              </a>
                            ) : (
                              <span className="text-gray-500 ml-2">No URL available</span>
                            )}
                            {doc.document_type && (
                              <span className="text-xs text-gray-500 ml-2">({doc.document_type})</span>
                            )}
                          </div>
                        ));
                      }
                      // Fallback: try to iterate as object
                      else {
                        return Object.entries(docs).map(([key, doc]: [string, any]) => {
                          // Skip metadata keys
                          if (['upload_timestamp', 'document_count', 'gcs_folder_structure', 'phone_number'].includes(key)) {
                            return null;
                          }
                          return (
                            <div key={key} className="border p-2 rounded">
                              <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
                              {doc && typeof doc === 'object' && (doc.file_url || doc.url) ? (
                                <a href={doc.file_url || doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                                  View Document ({doc.original_filename || doc.filename || 'File'})
                                </a>
                              ) : (
                                <span className="text-gray-500 ml-2">No URL available</span>
                              )}
                            </div>
                          );
                        }).filter(Boolean);
                      }
                    })()
                  ) : (
                    <p className="text-gray-500">No documents uploaded</p>
                  )}
                </div>
              </div>
              {showVerificationModal.verification_notes && (
                <div>
                  <strong>Notes:</strong> {showVerificationModal.verification_notes}
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              {showVerificationModal.verification_status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      const notes = prompt('What documents are needed?');
                      if (notes) handleRequestDocuments(showVerificationModal.id, notes);
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                  >
                    Request Documents
                  </button>
                  <button
                    onClick={() => handleApprove(showVerificationModal.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('Rejection reason:');
                      if (reason) handleReject(showVerificationModal.id, reason);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Reject
                  </button>
                </>
              )}
              {showVerificationModal.phone_numbers && (
                <button
                  onClick={() => {
                    const phoneId = showVerificationModal.phone_numbers?.id || showVerificationModal.phone_number_id;
                    if (!phoneId) {
                      alert('Phone number ID not found');
                      return;
                    }
                    const currentNickname = showVerificationModal.phone_numbers?.nickname || '';
                    const currentCost = showVerificationModal.phone_numbers?.monthly_cost?.toString() || '1000';
                    const newNickname = prompt('Edit nickname:', currentNickname);
                    const newCost = prompt('Edit monthly cost (INR):', currentCost);
                    if (newNickname !== null || newCost !== null) {
                      const updates: { nickname?: string; monthly_cost?: number } = {};
                      if (newNickname !== null && newNickname !== currentNickname) {
                        updates.nickname = newNickname;
                      }
                      if (newCost !== null && newCost !== currentCost) {
                        const parsedCost = parseFloat(newCost);
                        if (!isNaN(parsedCost)) {
                          updates.monthly_cost = parsedCost;
                        }
                      }
                      if (Object.keys(updates).length > 0) {
                        handleEditPhoneNumber(phoneId, updates);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Edit Phone Number
                </button>
              )}
              <button
                onClick={() => setShowVerificationModal(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
